import { useCallback, useMemo, useRef, useState } from 'react'

import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'

import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
} from '~/libs/localStorage'
import { AREA_CODE_OPTIONS, parseAreaCodes } from '~/models/area'

export type UseAreaSelectionResult = {
  /** 呼び出し元から渡された選択肢 (参照安定化後)。 */
  selectableAreaCodes: readonly AreaCode[]
  visibleAreaCodes: Set<AreaCode>
  setVisibleAreaCodes: (codes: Set<AreaCode>) => void
  /** 選択を未操作状態 (`selectableAreaCodes` 全件) へ戻し、保存した選択も消す。 */
  resetAreaSelection: () => void
}

type AreaSelection = {
  codes: Set<AreaCode>
  /** 前回の表示から復元した選択か (この表示中にユーザーが選び直していないか)。 */
  restored: boolean
}

function readSavedSelection(storageKey: string): AreaSelection | undefined {
  const raw = getLocalStorageItem(storageKey)
  const codes = raw === undefined ? undefined : parseAreaCodes(raw)
  return codes === undefined
    ? undefined
    : { codes: new Set(codes), restored: true }
}

/**
 * 呼び出し元が毎レンダー新しい配列 (中身は同じ) を渡しても、`visibleAreaCodes`
 * の参照を不要に変えない (呼び出し元が `useMemo` を挟み忘れても壊れない、
 * という本 hook の public contract。`BalanceDemand`/`BalanceGeneration` 双方の
 * `useFilterState.test.ts` の C-2 テストが固定する)。render 中の ref 比較で
 * 行う (`useEffect` を使わない、`usePersistedFilters.ts` の `initialFiltersRef`
 * と同型のパターン)。
 *
 * 本番の呼び出し元 (`useBusinessAreaFilter`) はすでに `useMemo` で参照安定な
 * 値を返すため、この安定化が無くても実害は無い。 それでも本 hook 内部に
 * private な実装詳細として残すのは、上記 contract を「呼び出し元の実装」に
 * 依存させないため (review 指摘 A-3: 以前はこのロジックを `useStableAreaCodes`
 * として public hook + 専用テストで公開していたが、そのテストが模していた
 * 「呼び出し元が `useMemo` を挟み忘れるケース」は実在の呼び出し元ではなく
 * テスト自身が作った仮想の caller だった。 契約を守るための実装詳細と、
 * 契約を公開 API として売ることは別であり、後者をやめて前者だけ残す)。
 */
function useStableReference<T>(value: readonly T[]): readonly T[] {
  const ref = useRef(value)
  const changed =
    ref.current.length !== value.length ||
    ref.current.some((item, i) => item !== value[i])
  if (changed) {
    ref.current = value
  }
  return ref.current
}

/**
 * エリアセレクターの選択状態 (issue #7535 / #7536、AC-BDB-011 ※ / AC-BDH-003 の
 * 共有ポリシー)。`BalanceDemand` / `BalanceGeneration` の両 `useFilterState` に
 * byte-identical に複製されていた「明示選択 state + 選択肢との積で実効選択を
 * 導出 + クリアで未操作状態へ戻す」を1箇所に統合する (review 指摘 A-1/A-2:
 * 複製されたポリシーは、どちらかの画面だけ更新すると業務エリア設定への
 * 追随・クリア挙動が画面間で乖離する)。
 *
 * state に持つのは「ユーザーの明示選択」と前回の表示から復元した選択だけ
 * (未操作 = undefined)。 業務エリア設定への追随 (初期値 = 選択肢全件、
 * 選択肢外は選択に残さない) は
 * `visibleAreaCodes` の render 時導出が担うため、props→state 同期の
 * `useEffect` を持たない (`docs/guides/frontend-coding-guide.yaml`
 * 「useEffect は外部システムとの同期専用とし、状態同期やデータ取得目的での
 * 多用を避ける」、review 指摘 C-1)。
 *
 * 前回の選択は `storageKey` の localStorage に置き、次に開いた時に復元する
 * (AC-BDB-011 ※ / AC-BDH-003「同じブラウザで前回の選択値があればそれを優先」、
 * issue #7606)。 URL クエリだけに頼らないのは、サイドメニューから素の URL で開く主動線で
 * 復元できないため。 URL への反映は AC-BDB-007 NOTE (#5090) の義務として別途残っており、
 * 実装時は URL と保存値の優先順位 (EN 判断範囲) を決める必要がある。 書き込みは選択を変えた
 * イベントの中で行い、 未操作の既定値は書かない (`usePersistedFilters` と同じ理由で、
 * 業務エリアの取得前のフォールバック値を保存値にしない)。
 *
 * テスト配置: 本 hook 単体の専用テストファイルは置かない。 分岐 (初期値 = 全件
 * / 選択肢外を選択から落とす / クリアで現在の選択肢へ戻る / 空集合への全解除が
 * 維持される / 参照安定性 / 前回の選択の復元) はすべて `BalanceDemand`/
 * `BalanceGeneration` の `hooks/useFilterState.test.ts` が composed hook 経由で
 * 検証している。
 */
export function useAreaSelection(
  selectableAreaCodes: readonly AreaCode[],
  storageKey: string,
): UseAreaSelectionResult {
  const stableSelectableAreaCodes = useStableReference(selectableAreaCodes)

  const [userSelection, setUserSelection] = useState<AreaSelection | undefined>(
    () => readSavedSelection(storageKey),
  )

  // 表示用の実効選択。 `stableSelectableAreaCodes` (呼び出し元が対象エリア ∩
  // 業務エリア設定で計算済み) との積を毎回取ることで、「選択肢外は選択に
  // 残さない」を導出だけで構造的に満たす — 選択肢が未確定 (ロード中) の間に
  // 操作した選択も、確定後は自動で正しい範囲に収まる。
  //
  // 復元した選択が選択肢と 1 つも重ならない時 (前回の後に業務エリアを変えた等) は
  // 既定 (選択肢全件) で開く。 空のまま開くと、 選んでいないのに「エリアが選択されて
  // いません」になる (conventions.md「業務エリアフィルタの原則」でも空選択は
  // 絞り込みなしの意味)。 この表示中に自分で全解除した空は維持する (C-1)。
  const visibleAreaCodes = useMemo(() => {
    const base = userSelection?.codes ?? new Set(stableSelectableAreaCodes)
    const selected = stableSelectableAreaCodes.filter((code) => base.has(code))
    return userSelection?.restored === true && selected.length === 0
      ? new Set(stableSelectableAreaCodes)
      : new Set(selected)
  }, [userSelection, stableSelectableAreaCodes])

  const setVisibleAreaCodes = useCallback(
    (codes: Set<AreaCode>): void => {
      setUserSelection({ codes, restored: false })
      setLocalStorageItem(
        storageKey,
        JSON.stringify(AREA_CODE_OPTIONS.filter((code) => codes.has(code))),
      )
    },
    [storageKey],
  )

  const resetAreaSelection = useCallback((): void => {
    setUserSelection(undefined)
    removeLocalStorageItem(storageKey)
  }, [storageKey])

  return {
    selectableAreaCodes: stableSelectableAreaCodes,
    visibleAreaCodes,
    setVisibleAreaCodes,
    resetAreaSelection,
  }
}
